'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { Button } from '@extern/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@extern/ui/form';
import { Input } from '@extern/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@extern/ui/input-otp';
import { InputPassword } from '@extern/ui/input-password';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { string, z } from 'zod';
import { AuthCard } from '@/app/(auth)/_partials/auth-card';
import { useSignIn } from '../_hooks/use-sign-in';
import { useSignInFlowStore } from '../_hooks/use-sign-in-flow-store';
const PasswordSchema = z.object({
  password: z.string().min(1, {
    message: 'Password is required'
  })
});
const CodeSchema = z.object({
  code: string().min(6, {
    message: 'Please enter a valid code'
  })
});
const BackupCodeSchema = z.object({
  code: z.string().min(1, {
    message: 'Please enter your backup code'
  })
});
export function SignInVerificationsControl() {
  const $ = _c(143);
  const {
    onSubmitPassword,
    onSignUpSubmitCode,
    onResendCode,
    onUseBackupCode,
    onForgotPassword,
    fetchStatus,
    signIn,
    goToStep,
    errors,
    secondFactorStrategy
  } = useSignIn();
  const firstFactorStrategy = useSignInFlowStore(_temp);
  const [resendTimeLeft, setResendTimeLeft] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      resolver: zodResolver(PasswordSchema),
      defaultValues: {
        password: ""
      },
      mode: "onChange"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const passwordForm = useForm(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      resolver: zodResolver(CodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const emailCodeForm = useForm(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      resolver: zodResolver(CodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const totpForm = useForm(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      resolver: zodResolver(BackupCodeSchema),
      defaultValues: {
        code: ""
      },
      mode: "onChange"
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const backupCodeForm = useForm(t3);
  let t4;
  let t5;
  if ($[4] !== resendTimeLeft) {
    t4 = () => {
      let timer;
      if (resendTimeLeft > 0) {
        timer = setTimeout(() => setResendTimeLeft(_temp2), 1000);
      }
      return () => clearTimeout(timer);
    };
    t5 = [resendTimeLeft];
    $[4] = resendTimeLeft;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  const isFirstFactorEmailCode = firstFactorStrategy === "email_code" && signIn.status === "needs_first_factor";
  let t6;
  if ($[7] !== signIn.supportedSecondFactors) {
    t6 = signIn.supportedSecondFactors?.some(_temp3) ?? false;
    $[7] = signIn.supportedSecondFactors;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const hasBackupCodeFactor = t6;
  if (isFirstFactorEmailCode) {
    let t7;
    if ($[9] !== emailCodeForm || $[10] !== onSignUpSubmitCode) {
      t7 = emailCodeForm.handleSubmit(async data => {
        const ok = await onSignUpSubmitCode(data.code);
        if (!ok) {
          emailCodeForm.reset();
        }
      });
      $[9] = emailCodeForm;
      $[10] = onSignUpSubmitCode;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    let t8;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <AuthCard.Header><AuthCard.Title>Verify your email</AuthCard.Title><AuthCard.Description>Use the verification link sent to your email address</AuthCard.Description></AuthCard.Header>;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== emailCodeForm.control) {
      t9 = <AuthCard.Content><div className="flex w-full flex-col items-center justify-center gap-y-2"><FormField control={emailCodeForm.control} name="code" render={_temp4} /></div></AuthCard.Content>;
      $[13] = emailCodeForm.control;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    const t10 = fetchStatus === "fetching";
    const t11 = fetchStatus === "fetching";
    let t12;
    if ($[15] !== t10 || $[16] !== t11) {
      t12 = <Button aria-label="Continue" className="w-full" disabled={t10} isLoading={t11} type="submit">Continue</Button>;
      $[15] = t10;
      $[16] = t11;
      $[17] = t12;
    } else {
      t12 = $[17];
    }
    const t13 = resendTimeLeft > 0;
    let t14;
    if ($[18] !== onResendCode) {
      t14 = async () => {
        setResendTimeLeft(60);
        await onResendCode();
      };
      $[18] = onResendCode;
      $[19] = t14;
    } else {
      t14 = $[19];
    }
    const t15 = resendTimeLeft > 0 ? `${resendTimeLeft}s` : "60s";
    let t16;
    if ($[20] !== t15) {
      t16 = <span className="text-role-info">Resend ({t15})</span>;
      $[20] = t15;
      $[21] = t16;
    } else {
      t16 = $[21];
    }
    let t17;
    if ($[22] !== t13 || $[23] !== t14 || $[24] !== t16) {
      t17 = <Button appearance="ghost" className="gap-0" disabled={t13} onClick={t14} type="button">Didn't receive a code? {t16}</Button>;
      $[22] = t13;
      $[23] = t14;
      $[24] = t16;
      $[25] = t17;
    } else {
      t17 = $[25];
    }
    let t18;
    if ($[26] !== t12 || $[27] !== t17) {
      t18 = <AuthCard.Footer>{t12}{t17}</AuthCard.Footer>;
      $[26] = t12;
      $[27] = t17;
      $[28] = t18;
    } else {
      t18 = $[28];
    }
    let t19;
    if ($[29] !== t18 || $[30] !== t9) {
      t19 = <AuthCard>{t8}{t9}{t18}</AuthCard>;
      $[29] = t18;
      $[30] = t9;
      $[31] = t19;
    } else {
      t19 = $[31];
    }
    let t20;
    if ($[32] !== t19 || $[33] !== t7) {
      t20 = <form className="w-full" onSubmit={t7}>{t19}</form>;
      $[32] = t19;
      $[33] = t7;
      $[34] = t20;
    } else {
      t20 = $[34];
    }
    let t21;
    if ($[35] !== emailCodeForm || $[36] !== t20) {
      t21 = <Form key="email-code-first" {...emailCodeForm}>{t20}</Form>;
      $[35] = emailCodeForm;
      $[36] = t20;
      $[37] = t21;
    } else {
      t21 = $[37];
    }
    return t21;
  }
  if (signIn.status !== "needs_second_factor" && signIn.status !== "needs_client_trust") {
    let t7;
    if ($[38] !== onSubmitPassword || $[39] !== passwordForm) {
      let t8;
      if ($[41] !== onSubmitPassword) {
        t8 = data_0 => onSubmitPassword(data_0.password);
        $[41] = onSubmitPassword;
        $[42] = t8;
      } else {
        t8 = $[42];
      }
      t7 = passwordForm.handleSubmit(t8);
      $[38] = onSubmitPassword;
      $[39] = passwordForm;
      $[40] = t7;
    } else {
      t7 = $[40];
    }
    let t8;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <AuthCard.Header><AuthCard.Title>Join a Community Committed to Your Career Success</AuthCard.Title><AuthCard.Description>Access company Externships, build skills, and explore careers on your terms.</AuthCard.Description></AuthCard.Header>;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    let t9;
    if ($[44] !== errors.fields.password) {
      t9 = t10 => {
        const {
          field: field_0
        } = t10;
        return <FormItem className="flex flex-col gap-2"><FormLabel>Password</FormLabel><FormControl><InputPassword autoCapitalize="none" autoComplete="password" autoCorrect="off" data-cy="password" id="password" key="password" placeholder="********" type="password" {...field_0} /></FormControl>{errors.fields.password ? <FormMessage>{errors.fields.password.message}</FormMessage> : <FormMessage />}</FormItem>;
      };
      $[44] = errors.fields.password;
      $[45] = t9;
    } else {
      t9 = $[45];
    }
    let t10;
    if ($[46] !== passwordForm.control || $[47] !== t9) {
      t10 = <AuthCard.Content><FormField control={passwordForm.control} name="password" render={t9} /></AuthCard.Content>;
      $[46] = passwordForm.control;
      $[47] = t9;
      $[48] = t10;
    } else {
      t10 = $[48];
    }
    const t11 = fetchStatus === "fetching";
    const t12 = fetchStatus === "fetching";
    let t13;
    if ($[49] !== t11 || $[50] !== t12) {
      t13 = <Button aria-label="Sign in with Email" className="w-full" data-cy="sign-in" disabled={t11} isLoading={t12} type="submit">Sign in with Email</Button>;
      $[49] = t11;
      $[50] = t12;
      $[51] = t13;
    } else {
      t13 = $[51];
    }
    let t14;
    if ($[52] !== onForgotPassword) {
      t14 = <Button appearance="ghost" onClick={onForgotPassword} type="button">Forgot your password?</Button>;
      $[52] = onForgotPassword;
      $[53] = t14;
    } else {
      t14 = $[53];
    }
    let t15;
    if ($[54] !== goToStep || $[55] !== signIn.supportedFirstFactors) {
      t15 = signIn.supportedFirstFactors && signIn.supportedFirstFactors.length > 1 ? <Button appearance="ghost" className="w-full" onClick={() => goToStep("choose-strategy")} type="button">Sign in with Email Code</Button> : null;
      $[54] = goToStep;
      $[55] = signIn.supportedFirstFactors;
      $[56] = t15;
    } else {
      t15 = $[56];
    }
    let t16;
    if ($[57] !== t14 || $[58] !== t15) {
      t16 = <div className="flex w-full flex-col gap-4">{t14}{t15}</div>;
      $[57] = t14;
      $[58] = t15;
      $[59] = t16;
    } else {
      t16 = $[59];
    }
    let t17;
    if ($[60] !== t13 || $[61] !== t16) {
      t17 = <AuthCard.Footer>{t13}{t16}</AuthCard.Footer>;
      $[60] = t13;
      $[61] = t16;
      $[62] = t17;
    } else {
      t17 = $[62];
    }
    let t18;
    if ($[63] !== t10 || $[64] !== t17) {
      t18 = <AuthCard>{t8}{t10}{t17}</AuthCard>;
      $[63] = t10;
      $[64] = t17;
      $[65] = t18;
    } else {
      t18 = $[65];
    }
    let t19;
    if ($[66] !== t18 || $[67] !== t7) {
      t19 = <form onSubmit={t7}>{t18}</form>;
      $[66] = t18;
      $[67] = t7;
      $[68] = t19;
    } else {
      t19 = $[68];
    }
    let t20;
    if ($[69] !== passwordForm || $[70] !== t19) {
      t20 = <Form key="password" {...passwordForm}>{t19}</Form>;
      $[69] = passwordForm;
      $[70] = t19;
      $[71] = t20;
    } else {
      t20 = $[71];
    }
    return t20;
  }
  if (secondFactorStrategy === "totp") {
    let t7;
    if ($[72] !== onSignUpSubmitCode || $[73] !== totpForm) {
      t7 = totpForm.handleSubmit(async data_1 => {
        const ok_0 = await onSignUpSubmitCode(data_1.code);
        if (!ok_0) {
          totpForm.reset();
        }
      });
      $[72] = onSignUpSubmitCode;
      $[73] = totpForm;
      $[74] = t7;
    } else {
      t7 = $[74];
    }
    let t8;
    if ($[75] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <AuthCard.Header><AuthCard.Title>Two-factor authentication</AuthCard.Title><AuthCard.Description>Enter the code from your authenticator app</AuthCard.Description></AuthCard.Header>;
      $[75] = t8;
    } else {
      t8 = $[75];
    }
    let t9;
    if ($[76] !== totpForm.control) {
      t9 = <AuthCard.Content><div className="flex w-full flex-col items-center justify-center gap-y-2"><FormField control={totpForm.control} name="code" render={_temp5} /></div></AuthCard.Content>;
      $[76] = totpForm.control;
      $[77] = t9;
    } else {
      t9 = $[77];
    }
    const t10 = fetchStatus === "fetching";
    const t11 = fetchStatus === "fetching";
    let t12;
    if ($[78] !== t10 || $[79] !== t11) {
      t12 = <Button aria-label="Continue" className="w-full" disabled={t10} isLoading={t11} type="submit">Continue</Button>;
      $[78] = t10;
      $[79] = t11;
      $[80] = t12;
    } else {
      t12 = $[80];
    }
    let t13;
    if ($[81] !== hasBackupCodeFactor || $[82] !== onUseBackupCode) {
      t13 = hasBackupCodeFactor ? <Button appearance="ghost" onClick={onUseBackupCode} type="button">Use a backup code instead</Button> : null;
      $[81] = hasBackupCodeFactor;
      $[82] = onUseBackupCode;
      $[83] = t13;
    } else {
      t13 = $[83];
    }
    let t14;
    if ($[84] !== t12 || $[85] !== t13) {
      t14 = <AuthCard.Footer>{t12}{t13}</AuthCard.Footer>;
      $[84] = t12;
      $[85] = t13;
      $[86] = t14;
    } else {
      t14 = $[86];
    }
    let t15;
    if ($[87] !== t14 || $[88] !== t9) {
      t15 = <AuthCard>{t8}{t9}{t14}</AuthCard>;
      $[87] = t14;
      $[88] = t9;
      $[89] = t15;
    } else {
      t15 = $[89];
    }
    let t16;
    if ($[90] !== t15 || $[91] !== t7) {
      t16 = <form className="w-full" onSubmit={t7}>{t15}</form>;
      $[90] = t15;
      $[91] = t7;
      $[92] = t16;
    } else {
      t16 = $[92];
    }
    let t17;
    if ($[93] !== t16 || $[94] !== totpForm) {
      t17 = <Form key="totp" {...totpForm}>{t16}</Form>;
      $[93] = t16;
      $[94] = totpForm;
      $[95] = t17;
    } else {
      t17 = $[95];
    }
    return t17;
  }
  if (secondFactorStrategy === "backup_code") {
    let t7;
    if ($[96] !== backupCodeForm || $[97] !== onSignUpSubmitCode) {
      t7 = backupCodeForm.handleSubmit(async data_2 => {
        const ok_1 = await onSignUpSubmitCode(data_2.code);
        if (!ok_1) {
          backupCodeForm.reset();
        }
      });
      $[96] = backupCodeForm;
      $[97] = onSignUpSubmitCode;
      $[98] = t7;
    } else {
      t7 = $[98];
    }
    let t8;
    if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <AuthCard.Header><AuthCard.Title>Use a backup code</AuthCard.Title><AuthCard.Description>Enter one of your saved backup codes to sign in</AuthCard.Description></AuthCard.Header>;
      $[99] = t8;
    } else {
      t8 = $[99];
    }
    let t9;
    if ($[100] !== backupCodeForm.control) {
      t9 = <AuthCard.Content><FormField control={backupCodeForm.control} name="code" render={_temp6} /></AuthCard.Content>;
      $[100] = backupCodeForm.control;
      $[101] = t9;
    } else {
      t9 = $[101];
    }
    const t10 = fetchStatus === "fetching";
    const t11 = fetchStatus === "fetching";
    let t12;
    if ($[102] !== t10 || $[103] !== t11) {
      t12 = <AuthCard.Footer><Button aria-label="Continue" className="w-full" disabled={t10} isLoading={t11} type="submit">Continue</Button></AuthCard.Footer>;
      $[102] = t10;
      $[103] = t11;
      $[104] = t12;
    } else {
      t12 = $[104];
    }
    let t13;
    if ($[105] !== t12 || $[106] !== t9) {
      t13 = <AuthCard>{t8}{t9}{t12}</AuthCard>;
      $[105] = t12;
      $[106] = t9;
      $[107] = t13;
    } else {
      t13 = $[107];
    }
    let t14;
    if ($[108] !== t13 || $[109] !== t7) {
      t14 = <form className="w-full" onSubmit={t7}>{t13}</form>;
      $[108] = t13;
      $[109] = t7;
      $[110] = t14;
    } else {
      t14 = $[110];
    }
    let t15;
    if ($[111] !== backupCodeForm || $[112] !== t14) {
      t15 = <Form key="backup-code" {...backupCodeForm}>{t14}</Form>;
      $[111] = backupCodeForm;
      $[112] = t14;
      $[113] = t15;
    } else {
      t15 = $[113];
    }
    return t15;
  }
  let t7;
  if ($[114] !== emailCodeForm || $[115] !== onSignUpSubmitCode) {
    t7 = emailCodeForm.handleSubmit(async data_3 => {
      const ok_2 = await onSignUpSubmitCode(data_3.code);
      if (!ok_2) {
        emailCodeForm.reset();
      }
    });
    $[114] = emailCodeForm;
    $[115] = onSignUpSubmitCode;
    $[116] = t7;
  } else {
    t7 = $[116];
  }
  let t8;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <AuthCard.Header><AuthCard.Title>Verify your email</AuthCard.Title><AuthCard.Description>Use the verification link sent to your email address</AuthCard.Description></AuthCard.Header>;
    $[117] = t8;
  } else {
    t8 = $[117];
  }
  let t9;
  if ($[118] !== emailCodeForm.control) {
    t9 = <AuthCard.Content><div className="flex w-full flex-col items-center justify-center gap-y-2"><FormField control={emailCodeForm.control} name="code" render={_temp7} /></div></AuthCard.Content>;
    $[118] = emailCodeForm.control;
    $[119] = t9;
  } else {
    t9 = $[119];
  }
  const t10 = fetchStatus === "fetching";
  const t11 = fetchStatus === "fetching";
  let t12;
  if ($[120] !== t10 || $[121] !== t11) {
    t12 = <Button aria-label="Continue" className="w-full" disabled={t10} isLoading={t11} type="submit">Continue</Button>;
    $[120] = t10;
    $[121] = t11;
    $[122] = t12;
  } else {
    t12 = $[122];
  }
  const t13 = resendTimeLeft > 0;
  let t14;
  if ($[123] !== onResendCode) {
    t14 = async () => {
      setResendTimeLeft(60);
      await onResendCode();
    };
    $[123] = onResendCode;
    $[124] = t14;
  } else {
    t14 = $[124];
  }
  const t15 = resendTimeLeft > 0 ? `${resendTimeLeft}s` : "60s";
  let t16;
  if ($[125] !== t15) {
    t16 = <span className="text-role-info">Resend ({t15})</span>;
    $[125] = t15;
    $[126] = t16;
  } else {
    t16 = $[126];
  }
  let t17;
  if ($[127] !== t13 || $[128] !== t14 || $[129] !== t16) {
    t17 = <Button appearance="ghost" className="gap-0" disabled={t13} onClick={t14} type="button">Didn't receive a code? {t16}</Button>;
    $[127] = t13;
    $[128] = t14;
    $[129] = t16;
    $[130] = t17;
  } else {
    t17 = $[130];
  }
  let t18;
  if ($[131] !== t12 || $[132] !== t17) {
    t18 = <AuthCard.Footer>{t12}{t17}</AuthCard.Footer>;
    $[131] = t12;
    $[132] = t17;
    $[133] = t18;
  } else {
    t18 = $[133];
  }
  let t19;
  if ($[134] !== t18 || $[135] !== t9) {
    t19 = <AuthCard>{t8}{t9}{t18}</AuthCard>;
    $[134] = t18;
    $[135] = t9;
    $[136] = t19;
  } else {
    t19 = $[136];
  }
  let t20;
  if ($[137] !== t19 || $[138] !== t7) {
    t20 = <form className="w-full" onSubmit={t7}>{t19}</form>;
    $[137] = t19;
    $[138] = t7;
    $[139] = t20;
  } else {
    t20 = $[139];
  }
  let t21;
  if ($[140] !== emailCodeForm || $[141] !== t20) {
    t21 = <Form key="email-code" {...emailCodeForm}>{t20}</Form>;
    $[140] = emailCodeForm;
    $[141] = t20;
    $[142] = t21;
  } else {
    t21 = $[142];
  }
  return t21;
}
function _temp7(t0) {
  const {
    field: field_3
  } = t0;
  return <FormItem className="w-full"><FormLabel>Email code</FormLabel><FormControl><InputOTP maxLength={6} {...field_3}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl><FormMessage className="text-center" /></FormItem>;
}
function _temp6(t0) {
  const {
    field: field_2
  } = t0;
  return <FormItem className="w-full"><FormLabel>Backup code</FormLabel><FormControl><Input autoCapitalize="none" autoComplete="one-time-code" autoCorrect="off" data-cy="backup-code" id="backup-code" placeholder="Enter backup code" {...field_2} /></FormControl><FormMessage className="text-center" /></FormItem>;
}
function _temp5(t0) {
  const {
    field: field_1
  } = t0;
  return <FormItem className="w-full"><FormLabel>Authenticator code</FormLabel><FormControl><InputOTP maxLength={6} {...field_1}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl><FormMessage className="text-center" /></FormItem>;
}
function _temp4(t0) {
  const {
    field
  } = t0;
  return <FormItem className="w-full"><FormLabel>Email code</FormLabel><FormControl><InputOTP maxLength={6} {...field}><InputOTPGroup className="h-full w-full"><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={0} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={1} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={2} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={3} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={4} /><InputOTPSlot className="min-h-14 w-1/6 font-medium text-base" index={5} /></InputOTPGroup></InputOTP></FormControl><FormMessage className="text-center" /></FormItem>;
}
function _temp3(factor) {
  return factor.strategy === "backup_code";
}
function _temp2(prev) {
  return prev - 1;
}
function _temp(s) {
  return s.firstFactorStrategy;
}